import { CloseOutlineIcon } from '@carboncopyco/icons';
import { TextBase } from '@carboncopyco/ui';
import { useAnalytics, useClickOutside } from '@carboncopyco/utils';
import type React from 'react';
import { useState } from 'react';
import { settings } from '../../../../settings';
import { AnalyticsEvents } from '../../../shared/constants/analytics.events';
import { isURL } from '../../../util/path-util';

const GoToAssetModal = ({
  onCloseModal,
  divRef,
}: {
  onCloseModal: () => void;
  divRef: React.RefObject<any>;
}) => {
  const [url, setUrl] = useState<string>('');
  const [isUrlValid, setIsUrlValid] = useState<boolean>(true);
  const { trackEvent } = useAnalytics();

  const convertURLToDeeplinkNavigateRoute = (submittedUrl: string) =>
    submittedUrl.replace(
      settings.WEB_APP,
      `${settings.DEEPLINK_PROTOCOL}://navigate`
    );

  const onSubmitUrl = (submittedUrl: string) => {
    const deeplink = convertURLToDeeplinkNavigateRoute(submittedUrl);
    window.dsDesktop.deeplink.goToDeeplink(deeplink);
    trackEvent(AnalyticsEvents.GO_TO_ASSET_WITH_LINK, {
      variables: {
        url: submittedUrl,
      },
    });
    onCloseModal();
  };

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === 'Escape') {
      onCloseModal();
      return;
    }
    if (event.key !== 'Enter') return;
    if (event.target instanceof HTMLInputElement) {
      event.target.blur();
    }
    if (!isURL(url) || !url.startsWith(settings.WEB_APP)) {
      setIsUrlValid(false);
      return;
    }
    onSubmitUrl(url);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isUrlValid) setIsUrlValid(true);
    setUrl(event.target.value);
  };

  const clearUrl = () => {
    setUrl('');
    setIsUrlValid(true);
  };

  const inputRef = useClickOutside(() => {
    onCloseModal();
  }, []);

  return (
    <div
      ref={divRef}
      className="backdrop-filter w-full h-full transform absolute left-1/2 -translate-x-1/2  z-9999 bg-black/40"
    >
      <div
        className={`right-auto h-12 border w-[700px] bg-white absolute left-1/4 top-20 ${
          isUrlValid ? 'border-ds-natural-200' : 'border-ds-danger'
        } px-2 rounded-lg flex flex-row align-center justify-between z-200`}
        ref={inputRef}
      >
        <input
          spellCheck={false}
          autoFocus
          className="text-lg truncate outline-none text-ellipsis w-full z-200 bg-white"
          value={url}
          placeholder="Go to asset"
          data-testid="go-to-asset-input"
          onKeyDown={onKeyDown}
          onChange={onInputChange}
        />
        <div
          className="w-4 h-4 bg-ds-natural-200 rounded-full cursor-pointer ml-2 z-200"
          onClick={clearUrl}
        >
          <CloseOutlineIcon
            size={16}
            color="#FFFF"
          />
        </div>
        {!isUrlValid && (
          <TextBase
            label="Invalid URL"
            color="text-ds-danger"
          />
        )}
      </div>
    </div>
  );
};

export default GoToAssetModal;
